import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import BottomSheetModal from '../../../components/BottomSheetModal';
import WithdrawText from './WithdrawText';
import type {
  IConfirmWithdrawComponent,
  IConfirmWithdrawData,
} from '../../../interfaces/Withdrawal';
import styles from '../styles.module.css';

interface ConfirmSheetProps {
  isOpen: boolean;
  data?: IConfirmWithdrawData;
  isSubmitting?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const TransferComponent: React.FC<{ component: IConfirmWithdrawComponent }> = ({
  component,
}) => (
  <div
    className={styles.confirmComponent}
    style={
      component.style?.bgColor
        ? { backgroundColor: component.style.bgColor }
        : undefined
    }
  >
    {component.headerData && (
      <div className={styles.confirmRow}>
        <WithdrawText data={component.headerData.keyData} className="fontSizeSm14" />
        <WithdrawText
          data={component.headerData.valueData}
          className="fontSizeSm14 fontWeightBold"
        />
      </div>
    )}

    {component.breakdowns?.map((row, index) => (
      <div className={styles.confirmRow} key={index}>
        <WithdrawText data={row.keyData} className="fontSizeSm12 fontColor666" />
        <WithdrawText data={row.valueData} className="fontSizeSm12" />
      </div>
    ))}

    {component.footer && (
      <WithdrawText
        data={component.footer}
        className={clsx(styles.confirmComponentFooter, 'fontSizeSm12')}
      />
    )}
  </div>
);

/**
 * The confirm sheet is built entirely from `confirmData` returned by the
 * breakdown call — no client-authored copy or totals.
 */
const ConfirmSheet: React.FC<ConfirmSheetProps> = ({
  isOpen,
  data,
  isSubmitting,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();

  const content = (
    <div className={styles.confirmBody}>
      {data?.transferComponents?.map((component, index) => (
        <TransferComponent component={component} key={index} />
      ))}

      {data?.footer && (
        <div
          className={styles.confirmFooter}
          style={
            data.footer.bgColor
              ? { backgroundColor: data.footer.bgColor }
              : undefined
          }
        >
          <WithdrawText data={data.footer.message} className="fontSizeSm12" />
        </div>
      )}
    </div>
  );

  return (
    <BottomSheetModal
      isOpen={isOpen}
      modalIcon={data?.iconUrl}
      title={data?.title?.text}
      content={content}
      onClose={onClose}
      actions={[
        {
          // Locked while submitting — this call moves real money and must not
          // fire twice.
          label: isSubmitting
            ? t('pleaseWait')
            : data?.ctaData?.text || t('confirmLabel'),
          onClick: isSubmitting ? () => {} : onConfirm,
          btnClass: clsx('primaryBtn', isSubmitting && styles.ctaBusy),
        },
      ]}
    />
  );
};

export default ConfirmSheet;
